import { MenuButtonProps, MenuItem } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import {
  useForceSendByPrefixId,
  useSkipByPrefixId,
  useRemoveByPrefixId,
  useActionDetailQueryByPrefixId,
} from "features/Resources/Shared/hooks";
import { useNavigate } from "react-router";
import { fromActionPrefixId, isProcessed, actionTemplatePathById } from "features/Resources/Shared/helpers";
import { PrefixId } from "features/Resources/Shared/models";
import { confirmRemoveArgs, confirmSendNowArgs } from "features/Resources/Shared/helpers/confirmationArgs";
import CaminoSkip from "components/Icon/CaminoSkip";
import CaminoSendNow from "components/Icon/CaminoSendNow";
import CaminoDelete from "components/Icon/CaminoDelete";
import Template from "components/Icon/Iconify/Template";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";

interface ResourceOptionMenuProps extends MenuButtonProps {
  prefixId: PrefixId;
  removeOnly?: boolean;
  includeRemove?: boolean;
  includeTemplate?: boolean;
}

export default function ResourceOptionMenu({
  prefixId,
  removeOnly = false,
  includeRemove = true,
  includeTemplate = false,
  ...rest
}: ResourceOptionMenuProps) {
  const { resourceType } = fromActionPrefixId(prefixId);

  const { data: resource } = useActionDetailQueryByPrefixId(prefixId);
  const navigate = useNavigate();

  const { mutate: handleForceSend } = useForceSendByPrefixId(prefixId);
  const { mutate: handleSkip } = useSkipByPrefixId(prefixId);
  const { mutate: handleRemove } = useRemoveByPrefixId(prefixId, () => {
    navigate("/");
  });
  if (!resource) return null;

  const processed = isProcessed(resource);
  // "failed" is still actionable — skip/retry/delete remain available.
  // Only truly terminal statuses (delivered, completed, skipped, removed) hide action items.
  const fullyProcessed = processed && resource.status !== "failed";

  if (fullyProcessed && !includeTemplate) return null;

  return (
    <VThreeDotMenu inPortal ariaLabel="Options" {...rest}>
      {!fullyProcessed && !removeOnly && (
        <MenuItem icon={<MenuItemIcon icon={CaminoSkip} />} onClick={() => handleSkip()}>
          {resource?.status === "skipped" ? "Unskip" : "Skip"}
        </MenuItem>
      )}
      {!fullyProcessed && !removeOnly && resource?.status !== "skipped" ? (
        <ButtonWithConfirm
          {...confirmSendNowArgs(resourceType, { isRerun: !!resource?.evaluatedAt })}
          intent="confirmation"
          handleConfirm={() => handleForceSend()}
        >
          <MenuItem icon={<MenuItemIcon icon={CaminoSendNow} />}>
            {resourceType === "task" ? "Start Now" : "Send Now"}
          </MenuItem>
        </ButtonWithConfirm>
      ) : null}
      {!fullyProcessed && (
        <ButtonWithConfirm
          {...confirmRemoveArgs(resourceType)}
          intent="confirmation"
          handleConfirm={() => handleRemove()}
        >
          <MenuItem icon={<MenuItemIcon icon={CaminoDelete} />}>Delete</MenuItem>
        </ButtonWithConfirm>
      )}
      {includeTemplate && (
        <MenuItem icon={<MenuItemIcon icon={Template} />} as={RouterLink} to={actionTemplatePathById(resource)}>
          View Template
        </MenuItem>
      )}
    </VThreeDotMenu>
  );
}
